import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components";
import { Chip, IconButton, Input, Option, Select, Spinner, Typography } from "@material-tailwind/react";
import { ArrowDownIcon, ArrowUpIcon, Edit2Icon, EyeIcon, UserIcon, XIcon } from "lucide-react";
import moment from "moment";
import { convertToPresentableText } from "../../utils/functions";
import { ACCOMMODATION_TYPES_ENUM, OPERATIONAL_STATUSES, OPERATIONAL_STATUSES_ENUM } from "../../utils/enums";
import { Link } from "react-router-dom";
import { useGetSeats } from "../../custom-hooks/use-seats";
import { InterpretOperationalStatus } from "../../utils/ui-interpreters";
import SeatCrudModal from "./crud-modal";

const SeatPage = () => {

  // State variables
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [filterStatus, setFilterStatus] = useState(OPERATIONAL_STATUSES.OPERATIONAL);
  const [filterSeatType, setFilterSeatType] = useState('');
  const [sort, setSort] = useState('-createdDate');

  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setIsTyping(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: seats, isLoading: isSeatsLoading, isError: isSeatsError } = useGetSeats('all', {
    // _page: page,
    // _limit: limit,
    _search: debouncedSearch,
    _sort: sort,
    seatType: filterSeatType,
    status: filterStatus,
    // ...filters
  });

  // Open modal for adding a new seat
  const openAddSeatModal = () => {
    setSelectedSeat(null);
    setModalOpen(true);
  };

  // Open modal for updating a seat
  const openUpdateSeatModal = (seat) => {
    setSelectedSeat(seat);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedSeat(null);
  };

  const interpretOccupency = (occupency) => {
    if (occupency === null) {
      return <Chip color="green" size="sm" className="text-xs" variant="ghost" value={`Full time`} />;
    }
    if (occupency.isFullyOccupied) {
      return <Chip color="red" size="sm" className="text-xs" variant="ghost" value={`Occupied`} />;
    }
    return <Chip color="blue" size="sm" className="text-xs" variant="ghost" value={`${occupency.occupiedHours} hours`} />;
  }

  const TABLE_COLUMNS = [
    {
      label: 'Seat #',
      key: 'seatCode',
      sortable: true,
      sort: sort === "seatCode" ? "-seatCode" : "seatCode"
    },
    {
      label: 'Space',
      key: 'space',
      sortable: false,
      sort: null
    },
    {
      label: 'Availability',
      key: 'availability',
      sortable: false,
      sort: null
    },
    {
      label: 'Curr Assign.',
      key: 'currentAssignment',
      sortable: false,
      sort: null
    },
    {
      label: 'Timestamp',
      key: 'createdDate',
      sortable: true,
      sort: sort === "createdDate" ? "-createdDate" : "createdDate"
    },
    {
      label: 'Actions',
      key: 'actions',
      sortable: false,
      sort: null
    }
  ]
  return (
    <div className={``}>
      <div className="m-5 p-5 border rounded-3xl">
        <PageHeader
          title="Seat Management"
          isLoading={isSeatsLoading}
          actionOnClick={openAddSeatModal}
          actionType="add"
          actionText="Add New Seat"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-2">
          <Input
            label="Search Seats"
            placeholder="Search by seat code "
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={isTyping ? <Spinner className="h-4 w-4" /> : <XIcon className={searchTerm ? "cursor-pointer text-gray-500" : "hidden"} size={16} onClick={() => setSearchTerm('')} />}
          />
          <Select
            label="Filter by Seat Type"
            value={filterSeatType}
            onChange={(value) => setFilterSeatType(value)}
          >
            {
              ACCOMMODATION_TYPES_ENUM.map((type, index) => (
                <Option key={index} value={type}>{convertToPresentableText(type)}</Option>
              ))
            }
          </Select>
          <Select
            label="Filter by Status"
            value={filterStatus}
            onChange={(value) => setFilterStatus(value)}
          >
            {OPERATIONAL_STATUSES_ENUM.map((status, index) => (
              <Option key={index} value={status}>{convertToPresentableText(status)}</Option>
            ))}
          </Select>
        </div>
        <div className="overflow-x-auto">
          {
            isSeatsLoading ?
              <div className="flex justify-center">
                <Spinner />
              </div> :
              <div className="overflow-x-auto mt-4">
                {isSeatsError ?
                  <div className="flex justify-center">
                    <p className="text-red-500">Error loading seats</p>
                  </div> : (
                    seats?.length > 0 ?
                      (
                        <table className="w-full min-w-max table-auto text-left">
                          <thead className="text-sm">
                            <tr className="py-3">
                              <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Seats ({seats?.length})</th>
                            </tr>
                            <tr className="py-3 capitalize">
                              {
                                TABLE_COLUMNS.map((column, index) => (
                                  <th
                                    key={index}
                                    className={"cursor-pointer py-3"}
                                    onClick={() => setSort(column.sortable ? column.sort : sort)}
                                  >
                                    <Typography
                                      variant="medium"
                                      color="blue-gray"
                                      className="flex items-center gap-2 font-bold leading-none opacity-70">
                                      {column.label} {column.sortable ? (column.sort.startsWith('-') ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />) : ''}
                                    </Typography>
                                  </th>
                                ))
                              }
                            </tr>
                          </thead>
                          <tbody>
                            {seats?.map((seat, index) => (
                              <tr key={index} draggable className={`border-t hover:bg-gray-50 button rounded-full ${seat.status === OPERATIONAL_STATUSES.CLOSED ? 'text-gray-500' : ''}`}>
                                <td className="py-3 text-sm">
                                  <div className="text-xs font-bold">
                                    {seat.seatCode} ({seat.seatType})
                                  </div>
                                  <div className="text-xs">
                                    {InterpretOperationalStatus(seat.status, "text")}
                                  </div>
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  <div className="font-bold text-sm">
                                    {seat.coworking?.code || 'N/A'}
                                  </div>
                                  <div className="text-xs text-gray-900">
                                    {seat.coworking?.floor?.floorCode} - {seat.coworking?.floor?.building?.name}
                                  </div>
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  {
                                    seat.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                      <div className="flex">
                                        {interpretOccupency(seat.occupency)}
                                      </div>
                                    ) : (
                                      <div className="flex">
                                        {InterpretOperationalStatus(seat.status)}
                                      </div>
                                    )
                                  }
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  {
                                    seat.currentBookingUsers && seat.currentBookingUsers.length > 0 ? (
                                      <div className="flex gap-1">
                                        {seat.currentBookingUsers.map(user => (
                                          <div className="flex gap-1" key={user._id}>
                                            <Link className="text-xs text-blue-600 hover:text-orange-600 duration-300 flex items-center gap-1" to={`/customer/${user._id}`} target="_blank">
                                              <UserIcon size={12} /> {user.name}
                                            </Link>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      seat.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                        <div className="flex">
                                          <Chip color="green" size="sm" className="text-xs" variant="ghost" value="Available" />
                                        </div>
                                      ) : (
                                        <div className="flex">
                                          {InterpretOperationalStatus(seat.status)}
                                        </div>
                                      )
                                    )
                                  }
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  <div className="flex flex-col">
                                    <div className="text-xs">C: {moment(seat.createdDate).format('lll')}</div>
                                    <div className="text-xs">U: {moment(seat.lastUpdated).format('lll')}</div>
                                  </div>
                                </td>
                                <td className="flex gap-1 py-3">
                                  <Link to={`/seat/${seat._id}`}>
                                    <IconButton
                                      size="sm"
                                      color="blue"
                                      className="rounded-full"
                                    >
                                      <EyeIcon size={12} />
                                    </IconButton>
                                  </Link>
                                  <IconButton
                                    size="sm"
                                    color="orange"
                                    className="rounded-full"
                                    onClick={() => openUpdateSeatModal(seat)}
                                  >
                                    <Edit2Icon size={12} />
                                  </IconButton>
                                </td>
                              </tr>
                            ))}
                          </tbody>

                        </table>
                      ) : (
                        <div className="flex justify-center">
                          <p className="text-red-500">No seats found</p>
                        </div>
                      )
                  )
                }
              </div>

          }
        </div>

        <SeatCrudModal
          isOpen={modalOpen}
          onClose={closeModal}
          seat={selectedSeat}
        />
      </div>
    </div>
  );
};

export default SeatPage;
